import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Loader2, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ApplyModal({ job, onClose }) {
  const [form, setForm] = useState({
    name: "", birthdate: "", city_state: "", email: "", phone: "",
    linkedin: "", portfolio: "", salary_expectation: "", availability: "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  // ESC key closes the modal
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    // Freeze body scroll while modal is open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!form.name || !form.linkedin) { toast.error("Nome e LinkedIn são obrigatórios."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/content/apply`, { job_id: job.id, job_title: job.title, ...form });
      setDone(true);
    } catch { toast.error("Não foi possível enviar. Tente novamente."); }
    finally { setBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="apply-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={onClose} />
      <motion.div initial={{ y: 40, scale: 0.95 }} animate={{ y: 0, scale: 1 }} className="relative bg-[#0e0e0e] border border-white/10 rounded-2xl max-w-2xl w-full max-h-[88vh] flex flex-col overflow-hidden">
        <div className="sticky top-0 z-20 bg-[#0e0e0e] border-b border-white/10 p-5 flex justify-between items-center shrink-0">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-accent-orange">Candidatura</div>
            <div className="font-display text-2xl">{job.title}</div>
            {job.location && <div className="text-xs text-neutral-500 mt-0.5">{job.location}</div>}
          </div>
          <button onClick={onClose} data-testid="apply-close"
            aria-label="Fechar candidatura"
            className="p-2.5 bg-white/10 hover:bg-accent-orange hover:text-black rounded-full transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable body — data-lenis-prevent stops Lenis from stealing wheel events */}
        <div className="flex-1 overflow-y-auto" data-lenis-prevent data-testid="apply-scroll">
        {done ? (
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="p-8 md:p-12 text-center" data-testid="apply-success">
            <div className="w-16 h-16 rounded-full bg-accent-orange/20 text-accent-orange mx-auto flex items-center justify-center mb-6">
              <Check className="w-8 h-8" />
            </div>
            <div className="font-display text-3xl mb-3">Entraremos em contato.</div>
            <p className="text-neutral-400 leading-relaxed max-w-md mx-auto">
              Recebemos sua candidatura para <b className="text-white">{job.title}</b>. Nosso time de gente & gestão vai analisar seu perfil com carinho. Se houver match, entraremos em contato pelos canais informados.
            </p>
            <div className="flex items-center justify-center gap-2 mt-6 text-accent-orange text-xs uppercase tracking-widest">
              <Sparkles className="w-3 h-3" /> Obrigado por querer ser Placer.
            </div>
            <button onClick={onClose} className="mt-8 bg-white/5 hover:bg-white/10 text-white text-sm px-6 py-3 rounded-full border border-white/10 transition">Fechar</button>
          </motion.div>
        ) : (
          <div className="p-6 md:p-8 space-y-4">
            <div className="grid md:grid-cols-2 gap-3">
              <F label="Nome completo *"><input value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="apply-name" className="w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm" /></F>
              <F label="Data de nascimento"><input type="date" value={form.birthdate} onChange={(e) => upd("birthdate", e.target.value)} data-testid="apply-birthdate" className="w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm" /></F>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <F label="Cidade / Estado"><input value={form.city_state} onChange={(e) => upd("city_state", e.target.value)} placeholder="Goiânia/GO" data-testid="apply-city" className="w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm" /></F>
              <F label="Telefone / WhatsApp"><input value={form.phone} onChange={(e) => upd("phone", e.target.value)} data-testid="apply-phone" className="w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm" /></F>
            </div>
            <F label="E-mail"><input type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="apply-email" className="w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm" /></F>
            <F label="LinkedIn *"><input value={form.linkedin} onChange={(e) => upd("linkedin", e.target.value)} placeholder="https://linkedin.com/in/seuperfil" data-testid="apply-linkedin" className="w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm" /></F>
            <F label="Portfolio (opcional)"><input value={form.portfolio} onChange={(e) => upd("portfolio", e.target.value)} placeholder="Behance, Dribbble, site pessoal…" data-testid="apply-portfolio" className="w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm" /></F>
            <div className="grid md:grid-cols-2 gap-3">
              <F label="Pretensão salarial"><input value={form.salary_expectation} onChange={(e) => upd("salary_expectation", e.target.value)} placeholder="R$ …" className="w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm" /></F>
              <F label="Disponibilidade">
                <select value={form.availability} onChange={(e) => upd("availability", e.target.value)} className="w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm">
                  <option value="">Selecione…</option>
                  <option value="imediata">Imediata</option>
                  <option value="15_dias">15 dias</option>
                  <option value="30_dias">30 dias</option>
                  <option value="60_dias">60 dias</option>
                </select>
              </F>
            </div>
            <F label="Por que você é ideal para essa vaga? (opcional)"><textarea value={form.message} onChange={(e) => upd("message", e.target.value)} rows={4} className="w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm" /></F>

            <button onClick={submit} disabled={busy} data-testid="apply-submit" className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-accent-orange to-accent-yellow hover:from-accent-yellow hover:to-accent-orange text-black font-bold py-3.5 rounded-full transition-all disabled:opacity-50 mt-2 shadow-2xl">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {busy ? "Enviando…" : "Enviar candidatura"}
            </button>
            <p className="text-[10px] text-neutral-500 text-center">Ao enviar, você concorda com a política de privacidade da Places.</p>
          </div>
        )}
        </div>
      </motion.div>
    </motion.div>
  );
}

function F({ label, children }) {
  return (<label className="block"><div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5">{label}</div>{children}</label>);
}
